<template>
  <Modal
    title="Email Quote"
    modal-classes="modal-xl"
    :disable-save="disableSave"
    @close="closeModal"
    @save="submitRecipients"
  >
    <div class="mb-4">
      <div
        class="d-flex flex-wrap align-items-start justify-content-between gap-3"
      >
        <div>
          <h5 class="mb-1">{{ optionTitle }}</h5>
          <p class="mb-0 text-muted">
            Select one or more contacts for this quote email.
          </p>
        </div>
        <span class="badge badge-alt-primary px-3 py-2">
          {{ selectedCountLabel }}
        </span>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <div class="mb-3">
      <label class="form-label" for="recipient-search">Search Contacts</label>
      <input
        id="recipient-search"
        v-model="searchTerm"
        type="text"
        class="form-control"
        placeholder="Search by name, email, or phone"
      />
    </div>

    <div class="rounded border overflow-hidden position-relative">
      <div
        v-if="isLoading"
        class="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
        style="z-index: 1"
      >
        <div class="spinner-border text-info" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div
        v-if="filteredContacts.length > 0"
        class="list-group list-group-flush"
      >
        <label
          v-for="contact in filteredContacts"
          :key="contact.id"
          class="list-group-item list-group-item-action d-flex align-items-start gap-3 py-3"
        >
          <input
            :id="`quote-email-contact-${contact.id}`"
            v-model="selectedContactIds"
            class="form-check-input mt-1"
            type="checkbox"
            :value="contact.id"
          />
          <div class="flex-grow-1">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
              <strong>{{ contact.name }}</strong>
              <span
                v-if="contact.id === defaultContactId"
                class="badge badge-alt-secondary rounded-pill"
              >
                Current quote contact
              </span>
            </div>
            <div class="text-muted small">
              <div v-if="contact.email">{{ contact.email }}</div>
              <div v-if="contact.phone">{{ contact.phone }}</div>
            </div>
          </div>
        </label>
      </div>

      <div v-else class="p-4 text-center text-muted">
        No matching contacts found for this customer.
      </div>
    </div>
  </Modal>
</template>

<script setup>
import { computed, ref } from "vue";
import Modal from "@/components/modal.vue";
import { useFetch } from "@/components/useFetch.js";

const props = defineProps({
  customer: {
    type: Object,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: () => ({
      id: null,
    }),
  },
  emailType: {
    type: String,
    required: true,
  },
  quoteType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["close", "send"]);

const contacts = ref([]);
const errorMessage = ref(null);
const isLoading = ref(false);
const searchTerm = ref("");
const defaultContactId = props.selectedContact?.id ?? null;
const selectedContactIds = ref(
  defaultContactId !== null ? [defaultContactId] : []
);

const optionTitle = computed(() => `${props.emailType} - ${props.quoteType}`);

const filteredContacts = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();

  if (term === "") {
    return contacts.value;
  }

  return contacts.value.filter((contact) => {
    return [contact.name, contact.email, contact.phone]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(term));
  });
});

const disableSave = computed(() => {
  return isLoading.value || selectedContactIds.value.length === 0;
});

const selectedCountLabel = computed(() => {
  const count = selectedContactIds.value.length;

  return count === 1 ? "1 contact selected" : `${count} contacts selected`;
});

fetchContacts();

async function fetchContacts() {
  if (!props.customer?.id) {
    errorMessage.value = "Select a customer before emailing a quote.";

    return;
  }

  isLoading.value = true;
  errorMessage.value = null;

  try {
    const response = await useFetch(
      route("api.customers.contacts", { customer: props.customer.id })
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message ?? "Failed to load contacts.");
    }

    contacts.value = result;

    if (
      defaultContactId !== null &&
      !contacts.value.some((contact) => contact.id === defaultContactId)
    ) {
      selectedContactIds.value = [];
    }
  } catch (error) {
    errorMessage.value = error.message ?? "Failed to load contacts.";
  } finally {
    isLoading.value = false;
  }
}

function closeModal() {
  emit("close");
}

function submitRecipients() {
  emit("send", {
    emailType: props.emailType,
    quoteType: props.quoteType,
    contactIds: selectedContactIds.value,
  });
}
</script>
